import { inject, ObjectGraph } from '@jet/environment/dependencies';
import { Logger } from './logger';
import { Bag } from './bag';
import { localizationResourcePath, type LanguageCode } from './bundle';
import { ObjectGraphType } from './object-graph-type';

// We're not importing the file into the runtime JavaScript, we're just
// importing the keys as a type, for build-time type checking.
// TODO: Update this to pull the `key` type from a nested `keys` object
//       The end result is to come up with a collection of loc keys that
//       we can type check if the value exists or not as an existing key.
export type LocalizationKey =
    keyof typeof import('../localization/localizable-strings.json');
export type LocalizedStringMap = Record<LocalizationKey, string>;
export type TokenReplacements = Record<string, string>;

export interface LocalizedStrings {
    languageCode: LanguageCode;
    strings: LocalizedStringMap;
}

interface PendingLocalizedStrings {
    languageCode: LanguageCode;
    loadingPromise: Promise<LocalizedStrings>;
}

export interface Localization {
    loadStrings(): Promise<LocalizedStrings>;
    readonly languageCode: LanguageCode;
    string(key: LocalizationKey, tokenReplacements?: TokenReplacements): string;
}

export class Localizer implements Localization {
    readonly partialObjectGraph: ObjectGraph;
    private localizedStrings?: LocalizedStrings;
    private pendingLocalizedStrings?: PendingLocalizedStrings;
    private static readonly fallbackLanguageCode = 'en-us';

    constructor(partialObjectGraph: ObjectGraph) {
        this.partialObjectGraph = partialObjectGraph;
    }

    async loadStrings(): Promise<LocalizedStrings> {
        const languageCode = this.languageCode;
        if (this.localizedStrings?.languageCode === languageCode) {
            return Promise.resolve(this.localizedStrings);
        }

        if (this.pendingLocalizedStrings?.languageCode === languageCode) {
            return await this.pendingLocalizedStrings.loadingPromise;
        }

        const loadingPromise = this.loadStringForLanguageCode(languageCode);
        this.pendingLocalizedStrings = {
            languageCode,
            loadingPromise,
        };
        const localizedStrings = await loadingPromise;
        // If the language changed after we started this load, we should
        // go through the loading process again.
        if (localizedStrings.languageCode !== this.languageCode) {
            Logger.log(
                this.partialObjectGraph,
                'Language changed while localization was loading, starting new load.',
            );
            return await this.loadStrings();
        }

        Logger.log(
            this.partialObjectGraph,
            `Localization resource "${
                localizedStrings.languageCode
            }" loaded with ${
                Object.keys(localizedStrings.strings).length
            } strings.`,
        );
        this.localizedStrings = localizedStrings;
        this.pendingLocalizedStrings = undefined;
        return Promise.resolve(localizedStrings);
    }

    string(
        key: LocalizationKey,
        tokenReplacements?: TokenReplacements,
    ): string {
        if (!this.localizedStrings) {
            throw new Error(
                `Localization.string(key:) called before localization was loaded, key "${key}"`,
            );
        }

        const locString = this.localizedStrings.strings[key];

        if (!tokenReplacements) {
            return locString;
        }

        return Localizer.replaceTokens(locString, tokenReplacements);
    }

    get languageCode(): LanguageCode {
        const languageTag = Bag.languageTag(this.partialObjectGraph);
        // Lowercasing a normalized language tag provided by the bag, *should* give us a valid language code supported
        // by the /localize endpoint, it's not ideal, but the team that owns the /localize endpoint is reluctant to
        // support normalized language tags: // rdar://problem/51500638 (/WebObjects/SRVLoc.woa/wa/localize support for
        // consuming normalized language tags).
        return languageTag.toLowerCase();
    }

    static string(
        key: LocalizationKey,
        objectGraph: ObjectGraph,
        tokenReplacements?: TokenReplacements,
    ): string {
        const localization = inject(ObjectGraphType.localization, objectGraph);
        return localization.string(key, tokenReplacements);
    }

    // MARK: Private

    private static replaceTokens(
        locString: string,
        replacements: TokenReplacements,
    ): string {
        let replacedLocalizedString = locString;

        Object.entries(replacements).forEach(([property, value]) => {
            // String.prototype.replace doesn't support $$ in replacements
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace#Specifying_a_string_as_a_parameter
            replacedLocalizedString = replacedLocalizedString
                .split(`@@${property}@@`)
                .join(value);
        });

        return replacedLocalizedString;
    }

    private async loadStringForLanguageCode(
        languageCode: LanguageCode,
    ): Promise<LocalizedStrings> {
        const bundle = inject(ObjectGraphType.bundle, this.partialObjectGraph);

        let localizationPath = localizationResourcePath(bundle, languageCode);
        if (!localizationPath) {
            Logger.error(
                this.partialObjectGraph,
                `Bundle missing "${languageCode}" localization, falling back to "${Localizer.fallbackLanguageCode}".`,
            );
            localizationPath = localizationResourcePath(
                bundle,
                Localizer.fallbackLanguageCode,
            );
        }

        if (!localizationPath) {
            throw new Error(
                `Failed to load localization, missing localization resources "${languageCode}", "${Localizer.fallbackLanguageCode}".`,
            );
        }

        Logger.log(
            this.partialObjectGraph,
            `Loading localization resource for language "${languageCode}"...`,
        );
        const strings = (await bundle.loadResource(
            localizationPath,
        )) as LocalizedStringMap;

        return {
            languageCode,
            strings,
        };
    }
}
